import type { TrackingCardData } from "../types"

/** Order tracking card — animated 4-stop progress timeline with the
 *  current step pulsed in cyan, ShipStation-enriched ETA + last-known
 *  location, line items, and a properly-contrasted Track Package CTA.
 *
 *  Replaces the previous text-only card that had white text on a white
 *  rc-cta button (unreadable) and no visual timeline. */
export function TrackingCard({ data }: { data: TrackingCardData }) {
  const stage = mapFulfillmentToStage(
    data.fulfillmentStatus,
    !!data.deliveredAt,
    !!data.lastEvent,
  )
  const eta = formatEta(data.eta, data.deliveredAt)

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 16,
        overflow: "hidden",
      }}
    >
      {/* Global keyframe for the current-stage pulse — injected here
       *  rather than in the CSS module because CSS modules hash
       *  keyframe names and the inline `animation:` ref needs the
       *  unhashed name to work. Idempotent (browsers dedupe @keyframes
       *  declarations with the same name). */}
      <style>{`
        @keyframes vw-tracking-pulse {
          0%   { transform: scale(0.85); opacity: 0.85; }
          70%  { transform: scale(1.55); opacity: 0; }
          100% { transform: scale(1.55); opacity: 0; }
        }
      `}</style>
      {/* Header — order number + status pill */}
      <div
        style={{
          padding: "14px 16px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              opacity: 0.6,
              marginBottom: 2,
            }}
          >
            Order {data.orderNumber}
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              lineHeight: 1.15,
              color: "rgba(255,255,255,0.97)",
            }}
          >
            {data.status}
          </div>
          {eta && (
            <div
              style={{
                fontSize: 12.5,
                color: "rgba(255,255,255,0.7)",
                marginTop: 2,
              }}
            >
              {eta}
            </div>
          )}
        </div>
        <StatusPill stage={stage} />
      </div>

      {/* Animated progress timeline */}
      <div style={{ padding: "16px 12px 12px" }}>
        <ProgressTimeline stage={stage} />
      </div>

      {/* Tracking detail strip — only when we have a tracking number */}
      {data.trackingNumber && (
        <div
          style={{
            padding: "10px 16px 6px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              opacity: 0.6,
            }}
          >
            Tracking
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12.5,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              color: "rgba(255,255,255,0.88)",
            }}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {data.trackingNumber}
            </span>
            {data.carrier && (
              <span
                style={{
                  fontFamily: "inherit",
                  opacity: 0.65,
                  fontSize: 11.5,
                }}
              >
                · {data.carrier}
              </span>
            )}
          </div>
          {data.lastEvent && (
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.4,
                marginTop: 2,
              }}
            >
              {data.lastEvent.description}
              {data.lastEvent.location ? ` · ${data.lastEvent.location}` : ""}
              {data.lastEvent.occurredAt &&
                ` · ${formatShort(data.lastEvent.occurredAt)}`}
            </div>
          )}
        </div>
      )}

      {/* Track Package CTA — white bg, dark text (matches the basket
       *  Checkout + summary CTAs). Previously used rc-cta which inherited
       *  --vw-cta-fg as white on the dark canvas — unreadable. */}
      {data.trackingUrl && (
        <div style={{ padding: "10px 16px 12px" }}>
          <a
            href={data.trackingUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 6,
              width: "100%",
              padding: "10px 14px",
              borderRadius: 11,
              background: "#fff",
              color: "#0d152c",
              fontSize: 13,
              fontWeight: 600,
              textDecoration: "none",
              letterSpacing: "-0.005em",
            }}
          >
            Track package
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      )}

      {/* Items in order — small list at the bottom */}
      {data.items && data.items.length > 0 && (
        <div
          style={{
            padding: "12px 16px 14px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              opacity: 0.6,
              marginBottom: 2,
            }}
          >
            In this order
          </div>
          {data.items.slice(0, 8).map((it, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                fontSize: 12.5,
                color: "rgba(255,255,255,0.84)",
                lineHeight: 1.35,
              }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={it.name}
              >
                {it.name}
              </span>
              {it.quantity > 1 && (
                <span style={{ opacity: 0.65, flexShrink: 0 }}>×{it.quantity}</span>
              )}
            </div>
          ))}
          {data.total && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 6,
                paddingTop: 8,
                borderTop: "1px solid rgba(255,255,255,0.06)",
                fontSize: 12.5,
              }}
            >
              <span style={{ opacity: 0.7 }}>Total</span>
              <span style={{ fontWeight: 700, color: "rgba(255,255,255,0.96)" }}>
                {data.total}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Progress timeline ────────────────────────────────────────────────

type Stage = 0 | 1 | 2 | 3 | 4   // Placed → Shipped → In Transit → Out for delivery → Delivered

const STAGES: { label: string }[] = [
  { label: "Placed" },
  { label: "Shipped" },
  { label: "In transit" },
  { label: "Out for delivery" },
  { label: "Delivered" },
]

function mapFulfillmentToStage(
  fulfillmentStatus: string,
  delivered: boolean,
  hasTrackingEvent: boolean,
): Stage {
  if (delivered) return 4
  const s = (fulfillmentStatus || "").toUpperCase()
  if (s === "OUT_FOR_DELIVERY") return 3
  if (s === "IN_TRANSIT") return 2
  if (s === "FULFILLED" || s === "PARTIALLY_FULFILLED") {
    // Shopify marks FULFILLED as soon as a label is created. If
    // ShipStation has seen at least one in-transit event, advance to
    // stage 2; otherwise stay at "Shipped".
    return hasTrackingEvent ? 2 : 1
  }
  if (s === "READY_FOR_PICKUP") return 3
  return 0   // UNFULFILLED / PROCESSING
}

function ProgressTimeline({ stage }: { stage: Stage }) {
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "0 6px",
      }}
    >
      {/* Track bar behind the dots — neutral grey + cyan-filled to the
       *  current stage. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 7,
          left: 16,
          right: 16,
          height: 2,
          background: "rgba(255,255,255,0.12)",
          borderRadius: 999,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 7,
          left: 16,
          height: 2,
          width: `calc((100% - 32px) * ${stage / (STAGES.length - 1)})`,
          background:
            "linear-gradient(90deg, rgba(109,195,247,0.85), rgba(72,130,235,0.95))",
          borderRadius: 999,
          transition: "width 600ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />
      {STAGES.map((s, i) => (
        <StageDot key={i} label={s.label} index={i} current={stage} />
      ))}
    </div>
  )
}

function StageDot({
  label,
  index,
  current,
}: {
  label: string
  index: number
  current: Stage
}) {
  const isPast = index < current
  const isCurrent = index === current
  const isDelivered = current === 4 && isCurrent
  return (
    <div
      style={{
        position: "relative",
        zIndex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        width: 64,
      }}
    >
      <div
        style={{
          position: "relative",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background:
            isPast || isCurrent
              ? "linear-gradient(135deg, rgba(109,195,247,0.95), rgba(72,130,235,1))"
              : "rgba(255,255,255,0.08)",
          border: "2px solid rgba(13,21,44,0.95)",
          boxShadow: isCurrent
            ? "0 0 0 4px rgba(109,195,247,0.25)"
            : "none",
        }}
      >
        {isDelivered && (
          <svg
            viewBox="0 0 24 24"
            width="10"
            height="10"
            fill="none"
            stroke="#0d152c"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
        {isCurrent && !isDelivered && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: -6,
              borderRadius: "50%",
              border: "2px solid rgba(109,195,247,0.55)",
              animation: "vw-tracking-pulse 1.6s ease-out infinite",
            }}
          />
        )}
      </div>
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.2,
          textAlign: "center",
          color: isCurrent
            ? "rgba(255,255,255,0.95)"
            : isPast
            ? "rgba(255,255,255,0.75)"
            : "rgba(255,255,255,0.5)",
          lineHeight: 1.2,
        }}
      >
        {label}
      </span>
    </div>
  )
}

// ── Status pill ───────────────────────────────────────────────────────

function StatusPill({ stage }: { stage: Stage }) {
  const palette =
    stage === 4
      ? { bg: "rgba(80, 200, 120, 0.22)", fg: "rgba(140, 240, 180, 0.95)", border: "rgba(80,200,120,0.45)" }
      : stage >= 1
      ? { bg: "rgba(109,195,247,0.22)", fg: "rgba(180,225,255,0.95)", border: "rgba(109,195,247,0.45)" }
      : { bg: "rgba(255,255,255,0.10)", fg: "rgba(255,255,255,0.85)", border: "rgba(255,255,255,0.18)" }
  const label =
    stage === 4 ? "Delivered" : stage === 3 ? "Out for delivery" : stage === 2 ? "In transit" : stage === 1 ? "Shipped" : "Processing"
  return (
    <span
      style={{
        flexShrink: 0,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.3,
        padding: "4px 10px",
        borderRadius: 999,
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  )
}

// ── Date helpers ──────────────────────────────────────────────────────

function formatEta(eta?: string, deliveredAt?: string): string | null {
  if (deliveredAt) return `Delivered ${formatLong(deliveredAt)}`
  if (!eta) return null
  return `Arrives ${formatLong(eta)}`
}

function formatLong(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    })
  } catch {
    return iso
  }
}

function formatShort(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}
